import Naluri from '~/common/api/naluri';
import { sortWith, descend, prop, groupBy } from 'ramda';
import {
  COMMON_SEARCH_CHANNELS_BEGIN,
  COMMON_SEARCH_CHANNELS_SUCCESS,
  COMMON_SEARCH_NEW_CHANNELS_SUCCESS,
  COMMON_SEARCH_TOREPLY_CHANNELS_SUCCESS,
  COMMON_SEARCH_CHANNELS_FAILURE,
  COMMON_SEARCH_CHANNELS_DISMISS_ERROR,
  COMMON_SEARCH_CHANNELS_ADD_CHANNEL,
  COMMON_SEARCH_CHANNELS_CLEAR,
} from './constants';

// Rekit uses redux-thunk for async actions by default: https://github.com/gaearon/redux-thunk
// If you prefer redux-saga, you can use rekit-plugin-redux-saga: https://github.com/supnate/rekit-plugin-redux-saga
export function searchChannels(filter, keyword, status, page) {
  return (dispatch, getState) => {
    // optionally you can have getState as the second argument
    dispatch({
      type: COMMON_SEARCH_CHANNELS_BEGIN,
    });

    // Return a promise so that you could control UI flow without states in the store.
    // For example: after submit a form, you need to redirect the page to another when succeeds or show some errors message if fails.
    // It's hard to use state to manage it, but returning a promise allows you to easily achieve it.
    // e.g.: handleSubmit() { this.props.actions.submitForm(data).then(()=> {}).catch(() => {}); }
    const promise = new Promise((resolve, reject) => {
      // doRequest is a placeholder Promise. You should replace it with your own logic.
      // See the real-word example at:  https://github.com/supnate/rekit/blob/master/src/features/home/redux/fetchRedditReactjsList.js
      // args.error here is only for test coverage purpose.
      const doRequest = Naluri.get(
        `/api/expert/search/channels?keyword=${keyword}&filter=${filter}&status=${status}&per_page=10&page=${page}`,
      );
      doRequest.then(
        res => {
          if (status === 'new') {
            dispatch({
              type: COMMON_SEARCH_NEW_CHANNELS_SUCCESS,
              data: res,
            });
          } else if (status === 'to_reply') {
            dispatch({
              type: COMMON_SEARCH_TOREPLY_CHANNELS_SUCCESS,
              data: res,
            });
          } else {
            dispatch({
              type: COMMON_SEARCH_CHANNELS_SUCCESS,
              data: res,
            });
          }

          resolve(res);
        },
        // Use rejectHandler as the second argument so that render errors won't be caught.
        err => {
          dispatch({
            type: COMMON_SEARCH_CHANNELS_FAILURE,
            data: { error: err },
          });
          reject(err);
        },
      );
    });

    return promise;
  };
}

export function addChannelsToSidebar(channel, user) {
  return (dispatch, getState) => {
    dispatch({
      type: COMMON_SEARCH_CHANNELS_ADD_CHANNEL,
      data: {
        channel: channel,
        user: user,
      },
    });
  };
}

export function clearSearchChannels() {
  return (dispatch, getState) => {
    dispatch({
      type: COMMON_SEARCH_CHANNELS_BEGIN,
    });

    dispatch({
      type: COMMON_SEARCH_CHANNELS_CLEAR,
    });
  };
}

// Async action saves request error by default, this method is used to dismiss the error info.
// If you don't want errors to be saved in Redux store, just ignore this method.
export function dismissGetUsersError() {
  return {
    type: COMMON_SEARCH_CHANNELS_DISMISS_ERROR,
  };
}

const filterUsers = (users, channels) => {
  const byUnread = sortWith([descend(prop('unreads_count'))]);
  const byStatus = sortWith([
    (a, b) => {
      const u1 = users.find(u => u.id === a.id);
      const u2 = users.find(u => u.id === b.id);
      const l1 = u1.status ? u1.status.length : 0;
      const l2 = u2.status ? u2.status.length : 0;

      return l2 - l1;
    },
  ]);

  const groupByStatusAndUnread = groupBy(a => {
    return a.unreads_count === 0 ? 'unread0' : a.status === 'online' ? 'online' : 'offline';
  });

  let onlineUsers = [];
  let offlineUsers = [];
  let unreadUsers = [];

  const groupedUsers = groupByStatusAndUnread(users);

  Object.keys(groupedUsers).forEach(key => {
    if (key === 'online') {
      onlineUsers = byUnread(groupedUsers[key]);
    } else if (key === 'offline') {
      offlineUsers = byUnread(groupedUsers[key]);
    } else {
      unreadUsers = byStatus(groupedUsers[key]);
    }
  });

  return [...onlineUsers, ...offlineUsers, ...unreadUsers];
};

const assignData = (users, channels) => {
  return users.map(user => {
    const channel = channels.find(c => c.id === user.cid);
    const last_message = channel
      ? channel.last_message
        ? channel.last_message.timestamp
        : Date.now()
      : Date.now();
    return {
      ...user,
      c_reply: channel ? channel.to_reply : false,
      c_type: channel ? channel.type : null,
      archived_status: channel ? channel.archived_status : false,
      last_message,
    };
  });
};

export function reducer(state, action) {
  switch (action.type) {
    case COMMON_SEARCH_CHANNELS_BEGIN:
      // Just after a request is sent
      return {
        ...state,
        searchChannelsPending: true,
        searchChannelsError: null,
      };

    case COMMON_SEARCH_CHANNELS_SUCCESS:
      // The request is success
      const usersPaginated =
        action.data.pagination && action.data.pagination.page === 1
          ? action.data.users
          : [...state.searchResults.users, ...action.data.users];

      const channelsPaginated =
        action.data.pagination && action.data.pagination.page === 1
          ? action.data.channels
          : [...state.searchResults.channels, ...action.data.channels];

      const assigned = assignData(usersPaginated, channelsPaginated);

      return {
        ...state,
        searchResults: {
          ...state.searchResults,
          users: usersPaginated,
          channels: channelsPaginated,
          pagination: action.data.pagination,
          filteredUserList: filterUsers(assigned, channelsPaginated),
        },
        searchChannelsPending: false,
        searchChannelsError: null,
      };

    case COMMON_SEARCH_NEW_CHANNELS_SUCCESS:
      // The request is success
      const usersNewPaginated =
        action.data.pagination && action.data.pagination.page === 1
          ? action.data.users
          : [...state.searchResults.usersNew, ...action.data.users];

      const channelsNewPaginated =
        action.data.pagination && action.data.pagination.page === 1
          ? action.data.channels
          : [...state.searchResults.channelsNew, ...action.data.channels];

      const assignedNew = assignData(usersNewPaginated, channelsNewPaginated);

      return {
        ...state,
        searchResults: {
          ...state.searchResults,
          usersNew: usersNewPaginated,
          channelsNew: channelsNewPaginated,
          paginationNew: action.data.pagination,
          filteredUserListNew: filterUsers(assignedNew, channelsNewPaginated),
        },
        searchChannelsPending: false,
        searchChannelsError: null,
      };

    case COMMON_SEARCH_TOREPLY_CHANNELS_SUCCESS:
      // The request is success
      const usersToReplyPaginated =
        action.data.pagination && action.data.pagination.page === 1
          ? action.data.users
          : [...state.searchResults.usersToReply, ...action.data.users];

      const channelsToReplyPaginated =
        action.data.pagination && action.data.pagination.page === 1
          ? action.data.channels
          : [...state.searchResults.channelsToReply, ...action.data.channels];

      const assignedToReply = assignData(usersToReplyPaginated, channelsToReplyPaginated);

      return {
        ...state,
        searchResults: {
          ...state.searchResults,
          usersToReply: usersToReplyPaginated,
          channelsToReply: channelsToReplyPaginated,
          paginationNew: action.data.pagination,
          filteredUserListToReply: filterUsers(assignedToReply, channelsToReplyPaginated),
        },
        searchChannelsPending: false,
        searchChannelsError: null,
      };

    case COMMON_SEARCH_CHANNELS_FAILURE:
      // The request is failed
      return {
        ...state,
        searchChannelsPending: false,
        searchChannelsError: action.data.error,
      };

    case COMMON_SEARCH_CHANNELS_DISMISS_ERROR:
      // Dismiss the request failure error
      return {
        ...state,
        searchChannelsError: null,
      };

    case COMMON_SEARCH_CHANNELS_ADD_CHANNEL:
      const channelFound = state.channels.find(c => c.id === action.data.channel.id);
      const userFound = state.users.find(
        u => u.id === action.data.user.id && u.cid === action.data.channel.id,
      );

      const channels = channelFound ? state.channels : [action.data.channel].concat(state.channels);
      const users = userFound ? state.users : [action.data.user].concat(state.users);
      const assignedUser = assignData(users, channels);
      const filtered = filterUsers(assignedUser, channels);

      return {
        ...state,
        channels,
        users,
        filteredUserList: filtered,
      };

    case COMMON_SEARCH_CHANNELS_CLEAR:
      // Clear search channel results
      return {
        ...state,
        searchResults: {
          users: [],
          channels: [],
          filteredUserList: [],
          pagination: {
            page: 1,
          },
          usersNew: [],
          channelsNew: [],
          filteredUserListNew: [],
          paginationNew: {
            page: 1,
          },
          usersToReply: [],
          channelsToReply: [],
          filteredUserListToReply: [],
          paginationToReply: {
            page: 1,
          },
        },
        searchChannelsPending: false,
        searchChannelsError: null,
      };

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/features/common/redux/searchChannels.js